import React, { useState } from 'react';
import { 
  FiClock, FiUser, FiPackage, FiAlertCircle, FiChevronRight,
  FiTruck, FiPhone, FiMessageSquare, FiCheck, FiPlay, FiPause
} from 'react-icons/fi';

// Effort badge colors and labels - High contrast solid colors with clear edges
const EFFORT_CONFIG = {
  1: { label: '1', color: 'bg-[#059669] text-white ring-1 ring-[#047857]', description: 'Quick task' },
  2: { label: '2', color: 'bg-[#0284C7] text-white ring-1 ring-[#0369A1]', description: 'Simple' },
  3: { label: '3', color: 'bg-[#D97706] text-white ring-1 ring-[#B45309]', description: 'Moderate' },
  4: { label: '4', color: 'bg-[#EA580C] text-white ring-1 ring-[#C2410C]', description: 'Complex' },
  5: { label: '5', color: 'bg-[#DC2626] text-white ring-1 ring-[#B91C1C]', description: 'Intensive' },
};

// Priority styles - High contrast solid backgrounds with clear edge definition
const PRIORITY_STYLES = {
  P0: {
    border: 'border-l-[#B91C1C]',
    bg: 'bg-white',
    gradient: 'bg-white border border-[#FECACA]',
    badge: 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D] shadow-[0_2px_6px_rgba(0,0,0,0.15)]',
    label: 'CRITICAL',
    glow: 'shadow-[0_8px_24px_rgba(0,0,0,0.12)]'
  },
  P1: {
    border: 'border-l-[#D97706]',
    bg: 'bg-white',
    gradient: 'bg-white border border-[#FDE68A]',
    badge: 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50 shadow-[0_2px_6px_rgba(0,0,0,0.12)]',
    label: 'HIGH',
    glow: 'shadow-[0_6px_20px_rgba(0,0,0,0.1)]'
  },
  P2: {
    border: 'border-l-[#6366F1]',
    bg: 'bg-white',
    gradient: 'bg-white border border-[#E4E4E7]',
    badge: 'bg-[#4F46E5] text-white ring-1 ring-[#3730A3]/50 shadow-[0_2px_6px_rgba(0,0,0,0.1)]',
    label: 'NORMAL',
    glow: 'shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
  },
};

// Ticket type icons
const TYPE_ICONS = {
  PLACE_ORDER: FiPackage,
  SET_DELIVERY_TIME: FiClock,
  VERIFY_ENTRY: FiUser,
  FIND_DRIVER: FiTruck,
  DRIVER_SUPPORT: FiMessageSquare,
  CONTACT_HOST: FiPhone,
  DEFAULT: FiAlertCircle,
};

export default function TicketCard({ 
  ticket, 
  isUrgent = false,
  isOverdue = false,
  isWorking = false,
  onStart,
  onComplete,
  onViewOrder,
  onPause,
  onClick,
  compact = false
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  const priority = ticket.priority || 'P2';
  const effort = Math.min(5, Math.max(1, ticket.effort || 3));
  const priorityStyle = PRIORITY_STYLES[priority] || PRIORITY_STYLES.P2;
  const effortConfig = EFFORT_CONFIG[effort];
  const TypeIcon = TYPE_ICONS[ticket.type] || TYPE_ICONS.DEFAULT;

  // Calculate time display
  const getTimeDisplay = () => {
    if (!ticket.dueAt) return null;
    const now = new Date();
    const due = new Date(ticket.dueAt);
    const diffMs = due - now;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 0) {
      return { text: `${Math.abs(diffMins)}m overdue`, isOverdue: true };
    } else if (diffMins < 60) {
      return { text: `${diffMins}m`, isUrgent: diffMins <= 15 };
    } else {
      const hours = Math.floor(diffMins / 60);
      return { text: `${hours}h ${diffMins % 60}m`, isUrgent: false };
    }
  };

  const timeDisplay = getTimeDisplay();

  const isNew = ticket.isNew;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick?.()}
      className={`
        relative rounded-2xl border-l-4 ${priorityStyle.border} ${priorityStyle.gradient}
        shadow-card hover:shadow-card-hover hover:-translate-y-0.5
        transition-all duration-300 cursor-pointer
        ${isUrgent ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
        ${isOverdue ? 'ring-2 ring-red-500 ring-offset-1' : ''}
        ${isWorking ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}
        ${isNew ? 'animate-bounce-in' : ''}
        ${compact ? 'p-3' : 'p-5'}
        ${priorityStyle.glow}
      `}
    >
      {/* Urgent/Overdue glow effect */}
      {(isUrgent || isOverdue) && (
        <div className={`absolute inset-0 rounded-xl ${isOverdue ? 'ticket-glow-red' : 'ticket-glow-amber'} pointer-events-none`} />
      )}

      {/* NEW badge for freshly added tickets */}
      {isNew && (
        <div className="absolute -top-2 -right-2 z-20">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#10B981] to-[#06B6D4] text-white ring-1 ring-[#047857]/50 shadow-[0_4px_12px_rgba(0,0,0,0.2)] animate-bounce">
            NEW
          </span>
        </div>
      )}

      <div className="relative z-10">
        {/* Header Row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            {/* Type Icon */}
            <div className={`
              w-10 h-10 rounded-xl flex items-center justify-center
              ${priority === 'P0' ? 'bg-[#FEE2E2] text-[#B91C1C] ring-1 ring-[#FECACA]' : 
                priority === 'P1' ? 'bg-[#FEF3C7] text-[#B45309] ring-1 ring-[#FDE68A]' : 
                'bg-[#F4F4F5] text-[#52525B] ring-1 ring-[#E4E4E7]'}
              ${isUrgent ? 'animate-pulse' : ''}
            `}>
              <TypeIcon className="w-5 h-5" />
            </div>

            <div>
              <h3 className="font-semibold text-zinc-900 text-sm leading-tight">
                {ticket.name || ticket.type}
              </h3>
              {ticket.ticketTypeId === 'DRIVER_SUPPORT' && ticket.customFieldValues?.isGeneral ? (
                <div className="text-xs text-purple-600 font-medium flex items-center gap-1">
                  <FiMessageSquare className="w-3 h-3" />
                  General Support
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); onViewOrder?.(ticket.orderId); }}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-mono hover:underline font-medium"
                >
                  Order #{ticket.orderUniqueId}
                </button>
              )}
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {/* Priority Badge */}
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${priorityStyle.badge}`}>
              {priorityStyle.label}
            </span>

            {/* General Support Badge */}
            {ticket.ticketTypeId === 'DRIVER_SUPPORT' && ticket.customFieldValues?.isGeneral && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 ring-1 ring-purple-200">
                GENERAL
              </span>
            )}

            {/* Effort Badge */}
            <span
              className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${effortConfig.color}`}
              title={`Effort: ${effortConfig.description}`}
            >
              {effortConfig.label}
            </span>
          </div>
        </div>

        {/* Info Row */}
        {!compact && (
          <div className="flex items-center gap-4 text-xs text-zinc-600 mb-3">
            {ticket.assignedToName && (
              <span className="flex items-center gap-1 font-medium">
                <FiUser className="w-3 h-3" />
                {ticket.assignedToName}
              </span>
            )}
            {ticket.category && (
              <span className="px-2 py-0.5 rounded-full bg-[#F4F4F5] text-[#3F3F46] font-medium ring-1 ring-[#E4E4E7]">
                {ticket.category}
              </span>
            )}
            {ticket.estimatedMinutes && (
              <span className="flex items-center gap-1 font-medium">
                <FiClock className="w-3 h-3" />
                ~{ticket.estimatedMinutes}m
              </span>
            )}
          </div>
        )}

        {/* Time & Actions Row */}
        <div className="flex items-center justify-between">
          {/* Time Display */}
          <div className="flex items-center gap-2">
            {timeDisplay && (
              <span className={`
                flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                ${timeDisplay.isOverdue ? 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D] shadow-[0_2px_6px_rgba(0,0,0,0.15)] animate-pulse' : 
                  timeDisplay.isUrgent ? 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50 shadow-[0_2px_6px_rgba(0,0,0,0.12)]' : 
                  'bg-[#52525B] text-white ring-1 ring-[#3F3F46] shadow-[0_2px_6px_rgba(0,0,0,0.08)]'}
              `}>
                <FiClock className="w-3 h-3" />
                {timeDisplay.isOverdue ? 'OVERDUE ' : 'Due '}{timeDisplay.text}
              </span>
            )}
            
            {/* Slack indicator */}
            {ticket.isSlackTicket && (
              <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#7C3AED] text-white text-xs font-bold ring-1 ring-[#5B21B6] shadow-[0_2px_6px_rgba(0,0,0,0.15)]">
                <FiMessageSquare className="w-3 h-3" />
                SLACK
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className={`flex items-center gap-2 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
            {isWorking ? (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); onPause?.(ticket); }}
                  className="p-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                  title="Pause"
                >
                  <FiPause className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onComplete?.(ticket); }}
                  className="p-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors"
                  title="Complete"
                >
                  <FiCheck className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onStart?.(ticket); }}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium transition-colors"
              >
                <FiPlay className="w-3 h-3" />
                Start
              </button>
            )}
          </div>
        </div>

        {/* Working Timer */}
        {isWorking && ticket.startedAt && (
          <div className="mt-3 pt-3 border-t border-[#E4E4E7]">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#065F46] font-semibold flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-[#10B981] animate-pulse ring-1 ring-[#059669]" />
                Working...
              </span>
              <span className="font-mono text-[#18181B] font-semibold bg-[#F4F4F5] px-2 py-1 rounded-lg ring-1 ring-[#E4E4E7]">
                {formatElapsed(new Date() - new Date(ticket.startedAt))}
              </span>
            </div>
          </div>
        )}

        {/* Expand indicator */}
        <div className={`absolute right-3 top-1/2 -translate-y-1/2 transition-opacity ${isHovered && !compact ? 'opacity-100' : 'opacity-0'}`}>
          <FiChevronRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    </div>
  );
}

function formatElapsed(ms) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}

